import { UNIVERSAL_BOOTSTRAP_OPTIONS_BY_PORTAL } from "../portals";
import type { UniversalBootstrapOpts } from "../universalBootstrap";

export type EntryName =
  | "mm"
  | "engage"
  | "cop"
  | "my"
  | "dashboard"
  | "connect";
export type EntryMapping<T> = { [K in EntryName]: T };

export type ShellEnvFromHTML = {
  MFE_CAT_URL: string;
  CONFIG_NAME: string;
};
export type PossiblyIncompleteShellEnv =
  | ShellEnvFromHTML
  | typeof window.SHELL_ENV;

export type AuthConfig =
  | { type: "kc" | "mm" | "tbi"; url?: string }
  | { type: "oidc"; url?: string; aud?: string };

export type FinalShellEnv = ShellEnvFromHTML & {
  AUTH_CONFIG: AuthConfig;
  API_STRATEGY: "engage" | "mm" | "cop";
  CONFIGCAT_SDK_KEY: string;
  DATADOG_APPLICATION_ID: string;
  DATADOG_CLIENT_TOKEN: string;
  API_BASE_URL: string;
  NEXTGEN_API_URL: string;
  ENGAGE_MFE_BASE_DOMAIN: string;
  SOCKET_HOST?: string; // Can potentially move into configcat
  CAMPAIGNS_HIVE_PROXY?: string;
  COMMIT_HASH: string;
  UNIVERSAL_BOOTSTRAP_OPTIONS?: UniversalBootstrapOpts;
};

function entryFromEnv(env: PossiblyIncompleteShellEnv): EntryName {
  const configName = env.CONFIG_NAME;
  const entry = configName.split(".")[0];
  if (
    entry === "mm" ||
    entry === "engage" ||
    entry === "cop" ||
    entry === "dashboard" ||
    entry === "my" ||
    entry === "connect"
  ) {
    return entry;
  }
  throw new Error(`Unknown ENTRY: ${entry}`);
}

/**
 * Represents the configuration settings for the application.
 */
class Config {
  public constructor(private fn: () => FinalShellEnv) {}

  /**
   * The complete Shell environment.
   */
  public get SHELL_ENV(): FinalShellEnv {
    return this.fn();
  }

  /**
   * The base domain for Engae MFEs
   */
  public get VENDOR_LABEL(): string | undefined {
    return window.VENDOR_LABEL;
  }

  /**
   * The base domain for Engae MFEs
   */
  public get ENGAGE_MFE_BASE_DOMAIN(): string {
    return this.SHELL_ENV.ENGAGE_MFE_BASE_DOMAIN;
  }

  /**
   * The Datadog application ID for datadog
   */
  public get DATADOG_APPLICATION_ID(): string {
    return this.SHELL_ENV.DATADOG_APPLICATION_ID;
  }

  /**
   * The Datadog client token.
   */
  public get DATADOG_CLIENT_TOKEN(): string {
    return this.SHELL_ENV.DATADOG_CLIENT_TOKEN;
  }

  /**
   * The authentication configuration.
   */
  public get AUTH_CONFIG(): AuthConfig {
    return this.SHELL_ENV.AUTH_CONFIG;
  }

  /**
   * The base URL for API requests.
   */
  public get API_BASE_URL(): string {
    return this.SHELL_ENV.API_BASE_URL;
  }

  /**
   * The ConfigCat SDK key.
   */
  public get CONFIGCAT_SDK_KEY(): string {
    return this.SHELL_ENV.CONFIGCAT_SDK_KEY;
  }

  /**
   * The socket host.
   */
  public get SOCKET_HOST(): string | undefined {
    return this.SHELL_ENV.SOCKET_HOST;
  }

  /**
   * The campaigns Hive proxy.
   */
  public get CAMPAIGNS_HIVE_PROXY(): string | undefined {
    return this.SHELL_ENV.CAMPAIGNS_HIVE_PROXY;
  }

  /**
   * The MFECat URL.
   */
  public get MFE_CAT_URL(): string {
    return this.SHELL_ENV.MFE_CAT_URL;
  }

  public get API_STRATEGY(): "engage" | "mm" | "cop" {
    return this.SHELL_ENV.API_STRATEGY;
  }

  public getEntry(): EntryName {
    return entryFromEnv(this.SHELL_ENV);
  }

  public get universalBootstrapOptions(): UniversalBootstrapOpts {
    if (this.SHELL_ENV.UNIVERSAL_BOOTSTRAP_OPTIONS != null) {
      return this.SHELL_ENV.UNIVERSAL_BOOTSTRAP_OPTIONS;
    }
    console.warn(
      "UNIVERSAL_BOOTSTRAP_OPTIONS not found in SHELL_ENV, using default options for entry"
    );
    return this.selectByEntry(UNIVERSAL_BOOTSTRAP_OPTIONS_BY_PORTAL);
  }

  /**
   * Takes an object that which has keys matching EntryNames and returns the value of the key that matches the current entry.
   *
   * @param obj The object to select from.
   */
  public selectByEntry<T>(obj: { [K in EntryName]: T }): T {
    return obj[this.getEntry()];
  }
}

// Just export the type Config, not the class Config itself
export type { Config };

export const config = new Config(() => window.SHELL_ENV);
